import { useEffect, useState } from 'react'
import {
  Upload as UploadIcon,
  FileText,
  Link as LinkIcon,
  Wand2,
  File,
  X,
  Cuboid,
  Info,
} from 'lucide-react'
import { Button } from '@lovable/components/ui/button'
import { Textarea } from '@lovable/components/ui/textarea'
import { Input } from '@lovable/components/ui/input'
import { Switch } from '@lovable/components/ui/switch'
import { Label } from '@lovable/components/ui/label'
import { toast } from 'sonner'
import { CreditCounter } from '@lovable/components/CreditCounter'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@lovable/components/ui/tooltip'
import { EngineForm } from '@/pages/engine/Engine'
import { SelectOption } from '@/common/components/Select/Select'
import { SingleValue } from 'react-select'
import { getThemesData } from '@/pages/engine/EngineHelper'
import { ThemeItem } from '@/pages/engine/ThemeSelector'
import { t } from 'i18next'

interface Props {
  onSubmit: (values: Partial<EngineForm>) => void
  has3D: boolean
  currentLanguage: SelectOption<string>
  onChangeLanguage: (selectOption: SingleValue<SelectOption>) => void
}

export const Upload: React.FC<Props> = ({ onSubmit, has3D, currentLanguage, onChangeLanguage }) => {
  const [files, setFiles] = useState<File[]>([])
  const [links, setLinks] = useState<string[]>([])
  const [linkInput, setLinkInput] = useState('')
  const [prompt, setPrompt] = useState('')
  const [is3DGame, setIs3DGame] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [uploadMode, setUploadMode] = useState<'files' | 'script'>('files')
  const [scriptFile, setScriptFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [themesData, setThemesData] = useState(getThemesData())

  useEffect(() => {
    setThemesData(getThemesData())
  }, [currentLanguage])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFiles((prev) => prev.concat(newFiles).slice(0, 3))
    }
  }

  const handleAddLink = () => {
    if (linkInput.trim()) {
      setLinks([...links, linkInput.trim()])
      setLinkInput('')
    }
  }

  const handleRemoveLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index))
  }

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const handleScriptFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setScriptFile(e.target.files[0])
    }
  }

  const handleRemoveScriptFile = () => {
    setScriptFile(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.currentTarget === e.target) {
      setIsDragging(false)
    }
  }

  const handleDrop = (e: React.DragEvent, mode: 'files' | 'script') => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)

    if (mode === 'files') {
      setFiles(droppedFiles)
    } else if (mode === 'script' && droppedFiles.length > 0) {
      setScriptFile(droppedFiles[0])
    }
  }

  const handleSubmit = () => {
    const hasFiles = files.length > 0
    const hasScriptFile = scriptFile !== null
    const hasLinks = links.length > 0
    const hasPrompt = prompt.trim().length > 0

    if (!hasFiles && !hasScriptFile && !hasLinks && !hasPrompt) {
      toast.error(t('engine.missing_materials', 'Please add at least one type of material to continue'))
      return
    }

    const materials = []
    if (hasFiles) materials.push(t('engine.material_added.files','%{amount} file(s)',{amount: files.length}))
    if (hasScriptFile) materials.push(t('engine.material_added.script','game script'))
    if (hasLinks) materials.push(t('engine.material_added.links','%{amount} link(s)',{amount: links.length}))
    if (hasPrompt) materials.push(t('engine.material_added.custom_prompt','custom prompt'))

    toast.success(
      t('engine.materials_uploaded', 'Materials uploaded: %{materials}', { materials: materials.join(', ') }),
    )

    const valuesToSubmit: Partial<EngineForm> = {
      webSources: links.join(','),
      freeText: prompt,
      inputFiles: hasScriptFile ? [scriptFile] : files,
      readyTasks: hasScriptFile,
      template: selectedTemplate || 'random',
    }

    onSubmit(valuesToSubmit)
    //navigate('/lense-selection')
  }
  return (
    <div className='min-h-screen w-full'>
      {/* Credit Counter Header */}
      <div className='sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border p-4 flex justify-end'>
        <CreditCounter language={currentLanguage} onChangeLanguage={onChangeLanguage} />
      </div>

      <div className='container max-w-5xl mx-auto p-8 animate-fade-in'>
        {/* Header */}
        <div className='mb-8 text-center'>
          <h1 className='text-4xl font-bold text-foreground mb-3'>
            {t('engine.main_title', "Let's Create Something Amazing!")}
          </h1>
          <p className='text-lg text-muted-foreground'>
            {t('engine.main_title_subtext', 'Create engaging learning games powered by AI')}
          </p>
        </div>

        {/* Unified Setup Form */}
        <div className='bg-card border border-border rounded-3xl p-8 shadow-xl animate-fade-in'>
          <div className='space-y-8'>
            {/* Section 1: Upload Materials */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center'>
                    <FileText className='w-4 h-4 text-primary' />
                  </div>
                  <div>
                    <h3 className='text-base font-semibold text-foreground'>
                      {t('engine.source_materials_title', 'Source Materials')}
                    </h3>
                    <p className='text-xs text-muted-foreground'>
                      {t('engine.source_materials_hint', 'Upload files or add a ready-made game script')}
                    </p>
                  </div>
                </div>

                <div className='flex items-center gap-3 bg-muted/50 rounded-lg p-1.5'>
                  <button
                    onClick={() => {
                      setUploadMode('files')
                      setScriptFile(null)
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      uploadMode === 'files'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('engine.files_tab', 'Files')}
                  </button>
                  <span className='text-muted-foreground text-xs'>{t('engine.materials_or','OR')}</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => {
                            setUploadMode('script')
                            setFiles([])
                          }}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                            uploadMode === 'script'
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          {t('engine.script_tab', 'Script (beta)')}
                          <Info className='w-3 h-3' />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side='bottom' className='max-w-xs'>
                        <p className='text-sm'>
                          {t(
                            'engine.script_tooltip',
                            'Upload a Word file with your ready-made tasks, story, points, and feedback ideas. Saige will read it, copy the details, and use them to build your game draft.',
                          )}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {uploadMode === 'files' ? (
                <label
                  htmlFor='file-upload'
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'files')}
                  className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                    isDragging
                      ? 'border-primary bg-primary/20 scale-[1.02]'
                      : 'border-border bg-muted/20 hover:bg-muted/40'
                  }`}
                >
                  <div className='flex flex-col items-center justify-center'>
                    <UploadIcon
                      className={`w-8 h-8 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-primary'}`}
                    />
                    <p className='text-sm text-foreground font-medium'>
                      <span className='font-semibold'>{t('engine.click_to_upload', 'Click to upload') + ' '}</span>
                      {t('engine.or_dnd', ' or drag and drop')}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {t('engine.file_formats', 'PDF, DOCX, PPTX, or TXT (MAX. 3 files, MAX. 10MB each)')}
                    </p>
                  </div>
                  <input
                    id='file-upload'
                    type='file'
                    className='hidden'
                    multiple
                    accept='.pdf,.doc,.docx,.ppt,.pptx,.txt'
                    onChange={handleFileChange}
                  />
                </label>
              ) : (
                <label
                  htmlFor='script-upload'
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, 'script')}
                  className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                    isDragging
                      ? 'border-primary bg-primary/20 scale-[1.02]'
                      : 'border-border bg-muted/20 hover:bg-muted/40'
                  }`}
                >
                  <div className='flex flex-col items-center justify-center'>
                    <FileText
                      className={`w-8 h-8 mb-2 transition-colors ${isDragging ? 'text-primary' : 'text-primary'}`}
                    />
                    <p className='text-sm text-foreground font-medium'>
                      <span className='font-semibold'>
                        {t('engine.click_to_upload_script', 'Click to upload game script')}
                      </span>
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {t('engine.file_format_script', 'Word document with tasks, story, and feedback (MAX. 10MB)')}
                    </p>
                  </div>
                  <input
                    id='script-upload'
                    type='file'
                    className='hidden'
                    accept='.doc,.docx,.pdf'
                    onChange={handleScriptFileChange}
                  />
                </label>
              )}

              {uploadMode === 'files' && files.length > 0 && (
                <div className='space-y-2'>
                  <p className='text-sm font-medium text-muted-foreground'>
                    {t('engine.selected_files', 'Selected files (%{amount}):', { amount: files.length })}
                  </p>
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className='flex items-center justify-between gap-2 p-2.5 bg-success/10 border border-success/30 rounded-lg group'
                    >
                      <div className='flex items-center gap-2 flex-1 min-w-0'>
                        <File className='w-4 h-4 text-success shrink-0' />
                        <span className='text-sm text-foreground truncate'>{file.name}</span>
                      </div>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleRemoveFile(index)}
                        className='opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-7 w-7 p-0'
                      >
                        <X className='w-3 h-3' />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {uploadMode === 'script' && scriptFile && (
                <div>
                  <p className='text-sm font-medium text-muted-foreground mb-2'>
                    {t('engine.selected_script', 'Game script:')}
                  </p>
                  <div className='flex items-center justify-between gap-2 p-2.5 bg-success/10 border border-success/30 rounded-lg group'>
                    <div className='flex items-center gap-2 flex-1 min-w-0'>
                      <FileText className='w-4 h-4 text-success shrink-0' />
                      <span className='text-sm text-foreground truncate'>{scriptFile.name}</span>
                    </div>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={handleRemoveScriptFile}
                      className='opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-7 w-7 p-0'
                    >
                      <X className='w-3 h-3' />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Divider */}
            {uploadMode === 'files' && <div className='border-t border-border/50' />}

            {/* Section 2: Resource Links */}
            {uploadMode === 'files' && (
              <div className='space-y-4'>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center'>
                    <LinkIcon className='w-4 h-4 text-secondary' />
                  </div>
                  <div>
                    <h3 className='text-base font-semibold text-foreground'>
                      {t('engine.resource_links_title', 'Resource Links')}
                    </h3>
                    <p className='text-xs text-muted-foreground'>
                      {t('engine.resource_links_hint', 'Add websites or articles (optional)')}
                    </p>
                  </div>
                </div>

                <div className='flex gap-2'>
                  <Input
                    type='url'
                    placeholder='https://example.com/resource'
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
                    className='flex-1 h-10 text-sm'
                  />
                  <Button
                    variant='outline'
                    onClick={handleAddLink}
                    disabled={!linkInput.trim()}
                    size='sm'
                    className='w-full sm:w-auto'
                  >
                    {t('engine.add', 'Add')}
                  </Button>
                </div>

                {links.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-sm font-medium text-muted-foreground'>
                      {t('engine.added_links', 'Added %{amount}:', { amount: links.length })}
                    </p>
                    {links.map((link, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between gap-2 p-2.5 bg-secondary/10 border border-secondary/30 rounded-lg group'
                      >
                        <div className='flex items-center gap-2 flex-1 min-w-0'>
                          <LinkIcon className='w-4 h-4 text-secondary shrink-0' />
                          <span className='text-sm text-foreground truncate'>{link}</span>
                        </div>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => handleRemoveLink(index)}
                          className='opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-7 w-7 p-0'
                        >
                          <X className='w-3 h-3' />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            {uploadMode === 'files' && <div className='border-t border-border/50' />}

            {/* Section 3: Description */}
            {uploadMode === 'files' && (
              <div className='space-y-4'>
                <div className='flex items-center gap-3'>
                  <div className='w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center'>
                    <Wand2 className='w-4 h-4 text-accent' />
                  </div>
                  <div>
                    <h3 className='text-base font-semibold text-foreground'>
                      {t('engine.describe_idea_title', 'Describe Your Idea')}
                    </h3>
                    <p className='text-xs text-muted-foreground'>
                      {t('engine.describe_idea_hint', 'What do you want to create? (optional)')}
                    </p>
                  </div>
                </div>

                <Textarea
                  placeholder={t(
                    'engine.idea_placeholder',
                    'Please design an interactive game that supports our team in putting the new strategy into action.',
                  )}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className='min-h-[120px] text-sm resize-none'
                />
              </div>
            )}

            {/* Divider */}
            <div className='border-t border-border/50' />

            {/* Section 4: 3D Game Option */}
            {has3D && (
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-3'>
                    <div className='w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center'>
                      <Cuboid className='w-4 h-4 text-primary' />
                    </div>
                    <div>
                      <h3 className='text-base font-semibold text-foreground'>
                        {t('engine.3d_game_title', '3D Game')}
                      </h3>
                      <p className='text-xs text-muted-foreground'>
                        {t('engine.3d_game_hint', 'Create an immersive 3D learning experience')}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Label htmlFor='3d-mode' className='text-sm text-muted-foreground'>
                      {t('engine.enable_3d', 'Enable 3D')}
                    </Label>
                    <Switch
                      id='3d-mode'
                      style={{ padding: '0px' }}
                      checked={is3DGame}
                      onCheckedChange={(checked) => {
                        setIs3DGame(checked)
                        if (!checked) setSelectedTemplate(null)
                      }}
                    />
                  </div>
                </div>

                {is3DGame && (
                  <div className='grid grid-cols-2 gap-3 animate-fade-in'>
                    {themesData.slice(6, 10).map((themeItem) => {
                      return (
                        <ThemeItem
                          onSelectTheme={() => {
                            setSelectedTemplate(themeItem.value)
                          }}
                          themeItem={themeItem}
                          selected={selectedTemplate === themeItem.value}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className='mt-6 flex justify-center'>
          <Button
            onClick={handleSubmit}
            size='lg'
            className='bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 transition-opacity shadow-lg px-12'
          >
            {t('engine.create_game_button', 'Create Game')}
          </Button>
        </div>
      </div>
    </div>
  )
}
